<template>
  <div class="cbs">
    <!-- Grupo 0: Arranque de la cobranza en Parkos -->
    <section class="cbs-group">
      <span class="cbs-group-title">Inicio de la cobranza</span>
      <BillingStartPicker
        v-model:mode="startMode"
        v-model:from="startFrom"
        v-model:assumed-paid="form.historyAssumedPaid"
      />
      <span class="cbs-start-warning">Los cargos que ya se generaron no se borran al cambiar esta opción. Si eliges un mes anterior, se agregan los meses que falten.</span>
    </section>

    <!-- Grupo 1: Reglas de vencimiento -->
    <section class="cbs-group">
      <span class="cbs-group-title">Reglas de vencimiento</span>
      <span class="cbs-group-sub">Define cuándo vence cada tipo de cargo. Estos valores aplican a todos los cargos nuevos generados a partir de ahora.</span>

      <div class="cbs-rule-card" style="border-left-color:#3b82f6">
        <div class="cbs-rule-card__title"><span class="mdi mdi-file-document-outline" style="color:#3b82f6"></span> Renta base</div>
        <div class="cbs-rule-card__controls">
          <div class="cbs-stepper">
            <span class="cbs-stepper-label">Día de vencimiento</span>
            <div class="cbs-stepper-control">
              <button @click="form.rentDueDay = Math.max(1, form.rentDueDay - 1)">−</button>
              <span>{{ form.rentDueDay }}</span>
              <button @click="form.rentDueDay = Math.min(28, form.rentDueDay + 1)">+</button>
            </div>
          </div>
          <select v-model="form.rentDueMonthMode" class="cbs-select">
            <option value="SAME_MONTH">Mes en curso</option>
            <option value="NEXT_MONTH">Mes siguiente</option>
          </select>
        </div>
        <span class="cbs-example">{{ rentExample }}</span>
      </div>

      <div class="cbs-rule-card" style="border-left-color:#10b981">
        <div class="cbs-rule-card__title"><span class="mdi mdi-cog-outline" style="color:#10b981"></span> Servicios comunes</div>
        <div class="cbs-stepper">
          <span class="cbs-stepper-label">Días de plazo desde el cierre</span>
          <div class="cbs-stepper-control">
            <button @click="form.serviceDueDays = Math.max(1, form.serviceDueDays - 1)">−</button>
            <span>{{ form.serviceDueDays }}</span>
            <button @click="form.serviceDueDays = Math.min(60, form.serviceDueDays + 1)">+</button>
          </div>
        </div>
        <span class="cbs-example">{{ serviceExample }}</span>
      </div>

      <div class="cbs-rule-card" style="border-left-color:#f0a500">
        <div class="cbs-rule-card__title"><span class="mdi mdi-wrench-outline" style="color:#f0a500"></span> Mantenimiento cobrado</div>
        <div class="cbs-stepper">
          <span class="cbs-stepper-label">Días de plazo desde el cierre del ticket</span>
          <div class="cbs-stepper-control">
            <button @click="form.maintenanceDueDays = Math.max(1, form.maintenanceDueDays - 1)">−</button>
            <span>{{ form.maintenanceDueDays }}</span>
            <button @click="form.maintenanceDueDays = Math.min(60, form.maintenanceDueDays + 1)">+</button>
          </div>
        </div>
        <span class="cbs-example">{{ maintenanceExample }}</span>
      </div>

      <div class="cbs-rule-card" style="border-left-color:#94a3b8">
        <div class="cbs-rule-card__title"><span class="mdi mdi-plus" style="color:#94a3b8"></span> Cargos manuales</div>
        <div class="cbs-stepper">
          <span class="cbs-stepper-label">Días de plazo sugerido</span>
          <div class="cbs-stepper-control">
            <button @click="form.manualChargeDueDaysDefault = Math.max(1, form.manualChargeDueDaysDefault - 1)">−</button>
            <span>{{ form.manualChargeDueDaysDefault }}</span>
            <button @click="form.manualChargeDueDaysDefault = Math.min(90, form.manualChargeDueDaysDefault + 1)">+</button>
          </div>
        </div>
        <span class="cbs-example">Vence {{ form.manualChargeDueDaysDefault }} días después de creado.</span>
        <span class="cbs-hint">Este valor pre-llena el formulario de nuevo cargo en cobranza pero el administrador siempre puede modificarlo por cargo individual.</span>
      </div>
    </section>

    <!-- Grupo 2: Umbrales -->
    <section class="cbs-group">
      <span class="cbs-group-title">Umbrales de alerta</span>
      <span class="cbs-group-sub">Define cuándo las métricas de cartera vencida cambian de color en el dashboard y en cobranza.</span>

      <div class="cbs-threshold-row">
        <div>
          <label class="cbs-label">Umbral de advertencia</label>
          <div class="cbs-input-suffix-wrap">
            <input v-model.number="form.overdueWarningPercent" type="number" class="cbs-input" :class="{ 'cbs-input--error': thresholdError }" />
            <span class="cbs-input-suffix">%</span>
          </div>
        </div>
        <span class="mdi mdi-arrow-right cbs-threshold-arrow"></span>
        <div>
          <label class="cbs-label">Umbral crítico</label>
          <div class="cbs-input-suffix-wrap">
            <input v-model.number="form.overdueCriticalPercent" type="number" class="cbs-input" :class="{ 'cbs-input--error': thresholdError }" />
            <span class="cbs-input-suffix">%</span>
          </div>
        </div>
      </div>
      <span v-if="thresholdError" class="cbs-error">Debe ser menor al umbral crítico</span>

      <div class="cbs-threshold-bar">
        <div class="cbs-threshold-bar__green" :style="{ width: form.overdueWarningPercent + '%' }"></div>
        <div class="cbs-threshold-bar__amber" :style="{ width: (form.overdueCriticalPercent - form.overdueWarningPercent) + '%' }"></div>
        <div class="cbs-threshold-bar__red" :style="{ width: (100 - form.overdueCriticalPercent) + '%' }"></div>
      </div>
      <div class="cbs-threshold-labels">
        <span style="color:#16a34a">Sin alerta: 0% a {{ form.overdueWarningPercent }}%</span>
        <span style="color:#d97706">Advertencia: {{ form.overdueWarningPercent }}% a {{ form.overdueCriticalPercent }}%</span>
        <span style="color:#dc2626">Crítico: {{ form.overdueCriticalPercent }}% a 100%</span>
      </div>
      <span class="cbs-hint">Porcentaje calculado sobre el ingreso total del mes. Si la cartera vencida supera el umbral crítico, la tarjeta KPI cambia a rojo en el dashboard y en finanzas.</span>
    </section>

    <!-- Grupo 3: Recordatorios automáticos -->
    <section class="cbs-group">
      <span class="cbs-group-title">Recordatorios automáticos</span>
      <span class="cbs-group-sub">El sistema enviará estas notificaciones automáticamente sin necesidad de acción manual.</span>

      <label class="cbs-toggle-row cbs-toggle-row--master">
        <input type="checkbox" v-model="form.remindersAutoEnabled" /> Activar recordatorios automáticos
      </label>

      <div class="cbs-reminders" :class="{ 'cbs-reminders--disabled': !form.remindersAutoEnabled }">
        <div class="cbs-reminder-row">
          <input type="checkbox" v-model="form.preventiveReminderEnabled" />
          <div class="cbs-reminder-info">
            <div class="cbs-reminder-name">Recordatorio preventivo</div>
            <div class="cbs-reminder-desc">Se envía automáticamente antes del vencimiento para recordar al inquilino su pago próximo.</div>
          </div>
          <div class="cbs-stepper">
            <span class="cbs-stepper-label">Días antes</span>
            <div class="cbs-stepper-control">
              <button @click="form.preventiveReminderDaysBefore = Math.max(1, form.preventiveReminderDaysBefore - 1)">−</button>
              <span>{{ form.preventiveReminderDaysBefore }}</span>
              <button @click="form.preventiveReminderDaysBefore = Math.min(15, form.preventiveReminderDaysBefore + 1)">+</button>
            </div>
          </div>
        </div>

        <div class="cbs-reminder-row">
          <input type="checkbox" v-model="form.brokenCommitmentAlertEnabled" />
          <div class="cbs-reminder-info">
            <div class="cbs-reminder-name">Alerta de compromiso incumplido</div>
            <div class="cbs-reminder-desc">Se activa cuando un inquilino no paga en la fecha prometida que registraste en el sistema.</div>
          </div>
          <div class="cbs-stepper">
            <span class="cbs-stepper-label">Horas de gracia</span>
            <div class="cbs-stepper-control">
              <button @click="form.brokenCommitmentGraceHours = Math.max(1, form.brokenCommitmentGraceHours - 1)">−</button>
              <span>{{ form.brokenCommitmentGraceHours }}</span>
              <button @click="form.brokenCommitmentGraceHours = Math.min(72, form.brokenCommitmentGraceHours + 1)">+</button>
            </div>
          </div>
        </div>

        <div class="cbs-reminder-row">
          <input type="checkbox" v-model="form.autoEscalateEnabled" />
          <div class="cbs-reminder-info">
            <div class="cbs-reminder-name">Escalación automática</div>
            <div class="cbs-reminder-desc">Genera una notificación formal automáticamente cuando la deuda supera el tiempo configurado.</div>
          </div>
          <div class="cbs-stepper">
            <span class="cbs-stepper-label">Días de mora</span>
            <div class="cbs-stepper-control">
              <button @click="form.autoEscalateDays = Math.max(15, form.autoEscalateDays - 1)">−</button>
              <span>{{ form.autoEscalateDays }}</span>
              <button @click="form.autoEscalateDays = Math.min(90, form.autoEscalateDays + 1)">+</button>
            </div>
          </div>
        </div>
      </div>
      <span class="cbs-hint">Los recordatorios automáticos se envían al contacto de pagos registrado en cada contrato. Puedes personalizar el destinatario por inquilino desde la página de Contratos.</span>
    </section>

    <button class="cbs-save-btn" :disabled="!dirty || thresholdError" @click="onSave">Guardar configuración de cobranza</button>

    <div class="cbs-impact-note">
      <span class="mdi mdi-information-outline"></span>
      Estos ajustes afectan las fechas de vencimiento en Cobranza, el bloque de costos en Mantenimiento, y la línea informativa de vencimiento en Contratos.
    </div>

    <Transition name="cbs-toast-fade">
      <div v-if="toastMessage" class="cbs-toast"><span class="mdi mdi-check-circle"></span> {{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, reactive } from 'vue'
import { getBillingConfig, saveBillingConfig } from '@/services/parkConfig'
import BillingStartPicker from '@/components/Onboarding/BillingStartPicker.vue'

const props = defineProps({ parkId: { type: String, required: true } })
const emit = defineEmits(['dirty-change'])

const form = reactive({
  rentDueDay: 5, rentDueMonthMode: 'SAME_MONTH', serviceDueDays: 15, maintenanceDueDays: 15, manualChargeDueDaysDefault: 15,
  overdueWarningPercent: 2, overdueCriticalPercent: 8,
  remindersAutoEnabled: true, preventiveReminderEnabled: true, preventiveReminderDaysBefore: 5,
  brokenCommitmentAlertEnabled: true, brokenCommitmentGraceHours: 24, autoEscalateEnabled: true, autoEscalateDays: 45,
  billingStartDate: null, historyAssumedPaid: true,
})

// El servidor guarda la fecha de arranque (ISO o null); el selector trabaja con modo + mes (YYYY-MM).
const startMode = computed({
  get: () => (form.billingStartDate ? 'HISTORY' : 'ZERO'),
  set: (v) => {
    if (v === 'ZERO') form.billingStartDate = null
    else if (!form.billingStartDate) form.billingStartDate = `${new Date().toISOString().slice(0, 7)}-01T00:00:00.000Z`
  },
})
const startFrom = computed({
  get: () => (form.billingStartDate ? String(form.billingStartDate).slice(0, 7) : ''),
  set: (v) => { form.billingStartDate = /^\d{4}-\d{2}$/.test(v || '') ? `${v}-01T00:00:00.000Z` : null },
})

const dirty = ref(false)
const toastMessage = ref(null)
let loaded = false

onMounted(async () => {
  const config = await getBillingConfig(props.parkId)
  if (config) Object.assign(form, config)
  loaded = true
})

watch(form, () => {
  if (!loaded) return
  dirty.value = true
  emit('dirty-change', true)
})

const thresholdError = computed(() => form.overdueWarningPercent >= form.overdueCriticalPercent)

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
const now = new Date()

const rentExample = computed(() => {
  const month = form.rentDueMonthMode === 'NEXT_MONTH' ? (now.getMonth() + 1) % 12 : now.getMonth()
  return `La renta de ${MONTHS[now.getMonth()]} vencerá el día ${form.rentDueDay} de ${MONTHS[month]}.`
})

const serviceExample = computed(() => {
  const due = new Date(now.getTime() + form.serviceDueDays * 24 * 60 * 60 * 1000)
  return `Si cierras servicios hoy ${now.getDate()} de ${MONTHS[now.getMonth()]}, el cargo vencerá el ${due.getDate()} de ${MONTHS[due.getMonth()]}.`
})

const maintenanceExample = computed(() => {
  const due = new Date(now.getTime() + form.maintenanceDueDays * 24 * 60 * 60 * 1000)
  return `Si cierras un ticket hoy ${now.getDate()} de ${MONTHS[now.getMonth()]} con cobro al inquilino, el cargo vencerá el ${due.getDate()} de ${MONTHS[due.getMonth()]}.`
})

async function onSave() {
  if (thresholdError.value) return
  await saveBillingConfig(props.parkId, { ...form })
  dirty.value = false
  emit('dirty-change', false)
  toastMessage.value = 'Configuración de cobranza actualizada · Los cambios aplican a cargos generados a partir de ahora · Los cargos existentes no se modifican.'
  setTimeout(() => (toastMessage.value = null), 4000)
}
</script>

<style scoped>
.cbs { display: flex; flex-direction: column; gap: 1.5rem; max-width: 640px; }
.cbs-group { display: flex; flex-direction: column; gap: 0.6rem; padding-bottom: 1.5rem; border-bottom: 1px solid #f1f5f9; }
.cbs-group-title { font-family: var(--font-body); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
.cbs-group-sub { font-family: var(--font-body); font-size: 11px; color: #94a3b8; margin-top: -0.4rem; }

.cbs-rule-card { background: #ffffff; border: 1px solid #e2e8f0; border-left: 3px solid; border-radius: 8px; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.4rem; }
.cbs-rule-card__title { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.cbs-rule-card__controls { display: flex; gap: 1rem; align-items: flex-end; }

.cbs-stepper { display: flex; flex-direction: column; gap: 0.2rem; }
.cbs-stepper-label { font-family: var(--font-body); font-size: 10px; color: #94a3b8; text-transform: uppercase; }
.cbs-stepper-control { display: flex; align-items: center; gap: 0.4rem; }
.cbs-stepper-control button { width: 28px; height: 28px; border-radius: 6px; border: 1px solid #e2e8f0; background: #f8f9fc; color: #1e3a5f; cursor: pointer; font-size: 14px; }
.cbs-stepper-control span { font-family: var(--font-mono); font-size: 13px; width: 24px; text-align: center; }

.cbs-select { height: 32px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0 0.5rem; font-family: var(--font-body); font-size: 12px; width: 160px; }
.cbs-example { font-family: var(--font-body); font-size: 11px; font-style: italic; color: #3b82f6; }
.cbs-hint { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }

.cbs-threshold-row { display: flex; align-items: flex-end; gap: 0.75rem; }
.cbs-label { display: block; font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; margin-bottom: 0.2rem; }
.cbs-input-suffix-wrap { display: flex; align-items: center; gap: 0.3rem; }
.cbs-input { width: 60px; font-family: var(--font-body); font-size: 13px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.5rem; }
.cbs-input--error { border-color: #e84545; }
.cbs-input-suffix { color: #94a3b8; font-size: 13px; }
.cbs-threshold-arrow { color: #94a3b8; font-size: 16px; margin-bottom: 0.6rem; }
.cbs-error { font-family: var(--font-body); font-size: 11px; color: #dc2626; }

.cbs-threshold-bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; margin-top: 0.4rem; }
.cbs-threshold-bar__green { background: #10b981; }
.cbs-threshold-bar__amber { background: #f0a500; }
.cbs-threshold-bar__red { background: #e84545; }
.cbs-threshold-labels { display: flex; justify-content: space-between; font-family: var(--font-body); font-size: 10px; margin-top: 0.3rem; }

.cbs-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.cbs-toggle-row--master { font-weight: 500; }

.cbs-reminders { display: flex; flex-direction: column; gap: 0.5rem; transition: opacity 150ms ease; }
.cbs-reminders--disabled { opacity: 0.5; pointer-events: none; }

.cbs-reminder-row { display: flex; align-items: center; gap: 0.75rem; height: 56px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0 0.9rem; }
.cbs-reminder-info { flex: 1; min-width: 0; }
.cbs-reminder-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.cbs-reminder-desc { font-family: var(--font-body); font-size: 11px; color: #64748b; }

.cbs-start-warning { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }
.cbs-save-btn { align-self: flex-start; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.6rem 1.2rem; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.cbs-save-btn:disabled { opacity: 0.4; cursor: default; }

.cbs-impact-note { display: flex; align-items: center; gap: 0.5rem; min-height: 48px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 0.6rem 1rem; font-family: var(--font-body); font-size: 11px; color: #1d4ed8; }

.cbs-toast { position: fixed; top: 1rem; right: 1rem; display: flex; align-items: center; gap: 0.4rem; background: #1e3a5f; color: #ffffff; padding: 0.6rem 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 3000; max-width: 400px; }
.cbs-toast .mdi { color: #10b981; flex-shrink: 0; }
.cbs-toast-fade-enter-active, .cbs-toast-fade-leave-active { transition: opacity 300ms ease; }
.cbs-toast-fade-enter-from, .cbs-toast-fade-leave-to { opacity: 0; }
</style>